import React, { useState } from 'react';
import { Box, TextField, IconButton, Button } from '@mui/material';
import { COLORS, UI, COMPONENT_COLORS } from '../config/colors';
import AIIcon from './AIIcon';

const Footer = ({
    onSendText,
    onSendVoice,
    onStopVoice,
    isRecording,
    disabled,
    leftSidebarOpen,
    drawerWidth,
    collapsedWidth = 50, // Make sure this matches the value in AppLayout.jsx
    rightSidebarOpen,
    rightSidebarWidth,
    placeholder = "Describe who you are looking for", // Add configurable placeholder
    voiceIconVariant = 'mic', // 'mic' | 'ai' (show AI logo button)
    voiceIconPx = 64, // applies when variant is 'ai'
    showVoiceButton = true // optionally hide voice button (for floating button usage)
}) => {
    const [inputText, setInputText] = useState('');

    const handleSendMessage = () => {
        if (inputText.trim()) {
            onSendText(inputText);
            setInputText('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleMic = () => {
        if (isRecording) {
            onStopVoice();
        } else {
            onSendVoice();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center', // Center the input box in the available space
                alignItems: 'center',
                width: '100%',
                padding: '0px 16px 8px', // Increased padding to match ChatInterface
                bgcolor: '#ffffff',
                position: 'relative',
                bottom: 0,
                zIndex: 100,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '12px',
                    bgcolor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #DCDFEA',
                    boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 4px 16px -4px rgba(16, 24, 40, 0.08)',
                    width: '800px', // Increased width to match wider ChatInterface
                    position: 'relative', // Add relative positioning for absolute placeholder
                }}
            >
                <TextField
                    fullWidth
                    placeholder={placeholder}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={disabled}
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                    }}
                    inputProps={{
                        style: {
                            fontSize: '16px',
                            fontFamily: 'Roboto',
                            color: COMPONENT_COLORS.mainContent.primary,
                        }
                    }}
                    className="footer-text-field" // Add a specific class name
                    sx={{
                        '& .MuiInputBase-root': {
                            padding: '0px',
                            position: 'relative',
                            zIndex: 2,
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '16px',
                            fontFamily: 'Roboto',
                            color: `${COMPONENT_COLORS.mainContent.primary}`,
                            caretColor: `${COMPONENT_COLORS.mainContent.primary}`,
                            backgroundColor: 'transparent',
                        },
                        // Multiple approaches to ensure placeholder visibility
                        '& .MuiInputBase-input::placeholder': {
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& .MuiInputBase-input::-webkit-input-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& .MuiInputBase-input::-moz-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& .MuiInputBase-input:-ms-input-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        // Additional fallback selectors
                        '& input::placeholder': {
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& input::-webkit-input-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& input::-moz-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                        '& input:-ms-input-placeholder': { 
                            color: '#969DAD !important',
                            opacity: '1 !important',
                        },
                    }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    {/* Left side buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                        <IconButton
                            sx={{
                                width: '32px',
                                height: '32px',
                                bgcolor: '#FFFFFF',
                                border: '1px solid #EAECF0',
                                borderRadius: '36px',
                                color: COMPONENT_COLORS.footer.secondary, // Using Figma color for footer icons
                                padding: '8px 12px',
                                '&:hover': {
                                    bgcolor: '#F9FAFB',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#FFFFFF',
                                    color: '#969DAD',
                                    opacity: 0.7,
                                },
                            }}
                            disabled={disabled}
                        >
                            <span className="material-icons-round" style={{ fontSize: '18px' }}>add</span>
                        </IconButton>
                    </Box>
                    
                    {/* Right side buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {showVoiceButton && (
                          <IconButton
                              onClick={toggleMic}
                              sx={{
                                  width: voiceIconVariant === 'ai' ? `${voiceIconPx}px` : '32px',
                                  height: voiceIconVariant === 'ai' ? `${voiceIconPx}px` : '32px',
                                  bgcolor: '#FFFFFF',
                                  border: '1px solid #EAECF0',
                                  borderRadius: '36px',
                                  color: isRecording ? COLORS.primary.red : COMPONENT_COLORS.footer.secondary, // Using Figma color for footer icons
                                  padding: voiceIconVariant === 'ai' ? '8px' : '8px 12px',
                                  '&:hover': {
                                      bgcolor: '#F9FAFB',
                                  },
                                  // Ensure AI icon inherits color if needed
                                  '& svg': {
                                      display: 'block'
                                  }
                              }}
                              aria-label={isRecording ? 'Stop voice' : 'Start voice'}
                          >
                              {voiceIconVariant === 'ai' ? (
                                  <AIIcon size={Math.max(24, voiceIconPx - 16)} />
                              ) : (
                                  <span className="material-icons-round" style={{ fontSize: '18px' }}>mic</span>
                              )}
                          </IconButton>
                        )}
                        
                        <IconButton
                            onClick={handleSendMessage}
                            sx={{
                                width: '32px',
                                height: '32px',
                                bgcolor: inputText.trim() ? '#4599FA !important' : '#BDD7F6 !important',
                                borderRadius: '36px',
                                color: inputText.trim() ? '#FFFFFF !important' : '#9CA3AF !important',
                                padding: '8px 12px',
                                '&:hover': {
                                    bgcolor: inputText.trim() ? '#3B82F6 !important' : '#BDD7F6 !important',
                                },
                            }}
                            disabled={!inputText.trim()}
                        >
                            <span className="material-icons-round" style={{ fontSize: '18px' }}>arrow_upward</span>
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Footer;
